import { api } from '@packages/backend/convex/_generated/api';
import { INTEGRATION_LABELS } from '@packages/shared';
import { createFileRoute, Link, Outlet, redirect } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import {
  Bot,
  ChevronRight,
  Clock,
  Globe,
  Package,
  Plug,
  Shield,
  Store,
  Tablet,
  Users,
} from 'lucide-react';
import type { ElementType } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { useIsSuperAdmin } from '@/features/auth/SuperAdminOnly';
import { isNavLinkVisible, type NavLink } from '@/routes/navLink';

export const Route = createFileRoute('/r/$restaurantId/s')({
  // Settings pads its own side nav and forms.
  staticData: { noPagePadding: true },
  beforeLoad: ({ location, params }) => {
    // Go to general settings by default
    if (location.pathname === `/r/${params.restaurantId}/s`) {
      throw redirect({
        params: { restaurantId: params.restaurantId },
        to: '/r/$restaurantId/s/general-settings',
      });
    }
  },
  component: SettingsLayout,
});

type SettingsNavLink = NavLink & {
  /** If true, only venues with a site (Zest venues) see this link */
  isSiteOnly?: boolean;
};

/**
 * The settings side nav (`/r/$restaurantId/s/*`). Super admin only links are hidden from restaurant users.
 * - Venue links: settings for the whole venue, whatever products it has. Integrations sit here
 *   too, since more than one product may use them.
 * - Product groups: pages only that product reads (`pos`, `online-ordering` in the feature flag
 *   registry). Not hidden by the product's flag yet: venues' product rows aren't backfilled (see the
 *   TODO in `featureFlagSchema.ts`).
 */
const venueNavLinks: SettingsNavLink[] = [
  { icon: Store, name: 'General settings', to: '/r/$restaurantId/s/general-settings' },
  { icon: Clock, name: 'Service hours', to: '/r/$restaurantId/s/service-hours' },
  { icon: Users, name: 'Users', to: '/r/$restaurantId/s/users' },
  { icon: Package, name: 'Features', to: '/r/$restaurantId/s/features' },
  { featureFlag: 'mcp-server', icon: Bot, name: 'AI agents', to: '/r/$restaurantId/s/ai-agents' },
  {
    featureFlag: 'square-integration',
    icon: Plug,
    name: INTEGRATION_LABELS.SQUARE,
    to: '/r/$restaurantId/s/square',
  },
  { isSuperAdminOnly: true, name: 'Feature flags', to: '/r/$restaurantId/s/feature-flags' },
];

const productNavGroups: { icon: ElementType; name: string; navLinks: SettingsNavLink[] }[] = [
  {
    icon: Tablet,
    name: 'POS',
    navLinks: [
      { name: 'iPads', to: '/r/$restaurantId/s/ipads' },
      { name: 'Printers', to: '/r/$restaurantId/s/pos-printers' },
      { name: 'Passcodes', to: '/r/$restaurantId/s/pos-passcodes' },
      { name: 'Shortcuts', to: '/r/$restaurantId/s/pos-price-adjustments' },
      { name: 'Floor plans', to: '/r/$restaurantId/s/floor-plans' },
    ],
  },
  {
    icon: Globe,
    name: 'Online ordering',
    navLinks: [
      {
        isSiteOnly: true,
        name: 'Website address',
        to: '/r/$restaurantId/s/website-address',
      },
      { name: 'Stripe setup', to: '/r/$restaurantId/s/stripe' },
      { name: 'Delivery', to: '/r/$restaurantId/s/delivery' },
      { name: 'Discount codes', to: '/r/$restaurantId/s/discount-codes' },
      { name: 'Loyalty card', to: '/r/$restaurantId/s/loyalty-card' },
      {
        isSuperAdminOnly: true,
        name: 'Platform settings',
        to: '/r/$restaurantId/s/platform-settings',
      },
      {
        isSuperAdminOnly: true,
        name: 'Mobile app config',
        to: '/r/$restaurantId/s/mobile-app-config',
      },
      { isSuperAdminOnly: true, name: 'Website config', to: '/r/$restaurantId/s/website-config' },
    ],
  },
];

const superAdminIcon = <Shield className="text-orange-400! fill-orange-100 dark:fill-orange-900" />;

function SettingsLayout() {
  const { restaurantId } = Route.useParams();
  const isSuperAdmin = useIsSuperAdmin();
  // The Website address page reads the same query, so it renders from the shared subscription
  const { data: site } = useQuery(api.sites.get, { restaurantId });
  const { data: featureFlags } = useQuery(api.featureFlags.listByRestaurantId, { restaurantId });
  const isVisible = (navLink: SettingsNavLink) =>
    isNavLinkVisible(navLink, isSuperAdmin, featureFlags) && (!navLink.isSiteOnly || !!site);

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-8 md:p-10">
      <div className="mx-auto grid w-full max-w-6xl gap-2">
        <h1 className="text-3xl font-semibold">Settings</h1>
      </div>
      <div className="mx-auto grid w-full max-w-6xl items-start gap-6 md:grid-cols-[220px_1fr] lg:grid-cols-[250px_1fr]">
        <nav aria-label="Settings">
          <SidebarMenu>
            {venueNavLinks.filter(isVisible).map((navLink) => (
              <SidebarMenuItem key={navLink.name}>
                <SidebarMenuButton asChild>
                  {/* The link marks itself active (`data-active`), so it follows navigation. A
                      `useMatchRoute` result doesn't: the React Compiler caches it. */}
                  <Link activeProps={{ 'data-active': true }} to={navLink.to}>
                    {navLink.isSuperAdminOnly ? superAdminIcon : navLink.icon && <navLink.icon />}
                    <span>{navLink.name}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
          <SidebarGroupLabel className="mt-4">Products</SidebarGroupLabel>
          <SidebarMenu>
            {productNavGroups.map((group) => (
              <Collapsible asChild className="group/collapsible" defaultOpen key={group.name}>
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <group.icon />
                      <span>{group.name}</span>
                      <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                    <SidebarMenuSub>
                      {group.navLinks.filter(isVisible).map((navLink) => (
                        <SidebarMenuSubItem key={navLink.name}>
                          <SidebarMenuSubButton asChild>
                            <Link activeProps={{ 'data-active': true }} to={navLink.to}>
                              {navLink.isSuperAdminOnly && superAdminIcon}
                              <span>{navLink.name}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            ))}
          </SidebarMenu>
        </nav>
        <div className="grid gap-6">
          <Outlet />

          {/*<Card>*/}
          {/*  <CardHeader>*/}
          {/*    <CardTitle>Plugins Directory</CardTitle>*/}
          {/*    <CardDescription>*/}
          {/*      The directory within your project, in which your plugins are located.*/}
          {/*    </CardDescription>*/}
          {/*  </CardHeader>*/}
          {/*  <CardContent>*/}
          {/*    <form className="flex flex-col gap-4">*/}
          {/*      <Input defaultValue="/content/plugins" placeholder="Project Name" />*/}
          {/*      <div className="flex items-center space-x-2">*/}
          {/*        <Checkbox defaultChecked id="include" />*/}
          {/*        <label*/}
          {/*          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"*/}
          {/*          htmlFor="include"*/}
          {/*        >*/}
          {/*          Allow administrators to change the directory.*/}
          {/*        </label>*/}
          {/*      </div>*/}
          {/*    </form>*/}
          {/*  </CardContent>*/}
          {/*  <CardFooter className="border-t">*/}
          {/*    <Button>Save</Button>*/}
          {/*  </CardFooter>*/}
          {/*</Card>*/}
        </div>
      </div>
    </div>
  );
}
